/**
 * Centralized Betterstack logging for CapLedger frontend
 * Sends structured logs to Betterstack (formerly Logtail) for monitoring and debugging
 * Get your source token from: https://betterstack.com/logs
 */

interface LogMeta {
  [key: string]: any;
  userId?: string;
  tenantId?: string;
  component?: string;
  action?: string;
}

interface LogEvent {
  level: 'info' | 'warn' | 'error' | 'debug';
  message: string;
  meta?: LogMeta;
  timestamp: string;
  environment: string;
}

const BETTERSTACK_LOGS_URL = 'https://in.logtail.com';

/**
 * Core function to send log events to Betterstack
 */
async function logEvent(event: LogEvent): Promise<void> {
  // Check new variable name first, then fall back to old name for backward compatibility
  const token =
    import.meta.env.VITE_BETTERSTACK_SOURCE_TOKEN || import.meta.env.VITE_LOGTAIL_SOURCE_TOKEN;

  // Skip if token is not configured (allows app to work without Betterstack)
  if (!token) {
    console.warn('[Logger] VITE_BETTERSTACK_SOURCE_TOKEN not configured, skipping Betterstack log');
    return;
  }

  try {
    await fetch(BETTERSTACK_LOGS_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(event),
    });
  } catch (error) {
    // Don't throw - logging should never break the app
    console.error('[Logger] Failed to send log to Betterstack:', error);
  }
}

/**
 * Get current environment and context info
 */
function getBaseContext(): Pick<LogEvent, 'environment' | 'timestamp'> {
  return {
    environment: import.meta.env.MODE || 'development',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Logger instance with convenience methods
 */
export const logger = {
  /**
   * Log informational messages
   */
  info(message: string, meta?: LogMeta): void {
    logEvent({
      level: 'info',
      message,
      meta,
      ...getBaseContext(),
    });
    console.info(`[INFO] ${message}`, meta || '');
  },

  /**
   * Log warnings
   */
  warn(message: string, meta?: LogMeta): void {
    logEvent({
      level: 'warn',
      message,
      meta,
      ...getBaseContext(),
    });
    console.warn(`[WARN] ${message}`, meta || '');
  },

  /**
   * Log errors with full context
   */
  error(message: string, error?: Error | unknown, meta?: LogMeta): void {
    const errorMeta = {
      ...meta,
      error:
        error instanceof Error
          ? {
              name: error.name,
              message: error.message,
              stack: error.stack,
            }
          : error,
    };

    logEvent({
      level: 'error',
      message,
      meta: errorMeta,
      ...getBaseContext(),
    });
    console.error(`[ERROR] ${message}`, error, meta || '');
  },

  /**
   * Log debug information
   */
  debug(message: string, meta?: LogMeta): void {
    logEvent({
      level: 'debug',
      message,
      meta,
      ...getBaseContext(),
    });
    console.debug(`[DEBUG] ${message}`, meta || '');
  },

  /**
   * Log audit events - mirrors to Betterstack for centralized monitoring
   * Use this specifically for audit trail events that need to be tracked
   */
  audit(message: string, meta?: LogMeta): void {
    logEvent({
      level: 'info',
      message,
      meta: {
        ...meta,
        eventType: 'audit',
        auditLog: true,
      },
      ...getBaseContext(),
    });
    console.info(`[AUDIT] ${message}`, meta || '');
  },

  /**
   * Create a logger instance with pre-filled context (userId, tenantId, component)
   */
  withContext(context: LogMeta) {
    return {
      info: (message: string, meta?: LogMeta) => logger.info(message, { ...context, ...meta }),
      warn: (message: string, meta?: LogMeta) => logger.warn(message, { ...context, ...meta }),
      error: (message: string, error?: Error | unknown, meta?: LogMeta) =>
        logger.error(message, error, { ...context, ...meta }),
      debug: (message: string, meta?: LogMeta) => logger.debug(message, { ...context, ...meta }),
    };
  },
};
